import { useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';

/**
 * What GET /api/athlete-lessons returns: the signed-in athlete's own lessons,
 * coming first then past, with no cancelled ones (ruled 16 October). The note
 * is absent until the coach closes the lesson; the server decides that.
 */
export interface AthleteLesson {
  lesson_id: string;
  start: string;
  end: string;
  outcome: 'booked' | 'completed' | 'no_show';
  service_name: string;
  product_name: string | null;
  /** A clinic's product_name is its title. */
  is_clinic: boolean;
  facility_name: string;
  coach_name: string;
  practice_plan: string | null;
  notes: string | null;
  /** Swings Recul counted in this lesson; null while it never counted. */
  swing_count: number | null;
}

export type AthleteLessonsState =
  | { status: 'loading' }
  | { status: 'ready'; lessons: AthleteLesson[] }
  | { status: 'failed'; message: string };

export const OUTCOME_LABEL: Record<AthleteLesson['outcome'], string> = {
  booked: 'Booked',
  completed: 'Completed',
  no_show: 'No show',
};

/** Both athlete screens read this one list, as the parent's all read /api/bookings. */
export function useAthleteLessons(): AthleteLessonsState {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<AthleteLessonsState>({ status: 'loading' });

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setState({ status: 'loading' });

    apiFetch<{ lessons: AthleteLesson[] }>('/api/athlete-lessons', getToken)
      .then((body) => {
        if (!cancelled) setState({ status: 'ready', lessons: body.lessons });
      })
      .catch((cause: unknown) => {
        if (cancelled || cause instanceof NotSignedIn) return;
        setState({
          status: 'failed',
          message: cause instanceof ApiError ? cause.message : 'Your lessons could not be loaded. Try again shortly.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken]);

  return state;
}
